/**
 * Life Stats — Impactful life statistics with animated counters.
 * Lived days/weeks/hours, heartbeats, remaining time.
 */

import { memo, useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Heart, Clock, Calendar, Zap } from 'lucide-react'
import {
  calcDaysLived,
  calcDaysRemaining,
  calcWeeksLived,
  calcWeeksRemaining,
  calcHeartbeats,
  calcLifePercentage,
  calcSecondsLived,
  formatLargeNumber,
  getMotivationalQuote,
} from '@turnifly/shared/utils'

interface LifeStatsProps {
  birthDate: string
  lifeExpectancy: number
}

export const LifeStats = memo(function LifeStats({ birthDate, lifeExpectancy }: LifeStatsProps) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language

  const daysLived = calcDaysLived(birthDate)
  const daysRemaining = calcDaysRemaining(birthDate, lifeExpectancy)
  const weeksLived = calcWeeksLived(birthDate)
  const weeksRemaining = calcWeeksRemaining(birthDate, lifeExpectancy)
  const heartbeats = calcHeartbeats(birthDate)
  const lifePercent = calcLifePercentage(birthDate, lifeExpectancy)
  const quote = getMotivationalQuote(lang)

  // Live seconds counter
  const [seconds, setSeconds] = useState(() => calcSecondsLived(birthDate))
  const rafRef = useRef<number | null>(null)
  const lastSecRef = useRef(-1)

  useEffect(() => {
    const tick = () => {
      const now = Math.floor(Date.now() / 1000)
      if (now !== lastSecRef.current) {
        lastSecRef.current = now
        setSeconds(calcSecondsLived(birthDate))
      }
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => { if (rafRef.current !== null) cancelAnimationFrame(rafRef.current) }
  }, [birthDate])

  return (
    <div className="space-y-3">
      {/* ── Life percentage hero ── */}
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.03] p-4">
        <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-primary/8 blur-[70px] pointer-events-none" />

        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-hint">{t('life.lifeProgress')}</span>
          <span className="text-sm font-display font-extrabold text-primary tabular-nums">{lifePercent}%</span>
        </div>

        {/* Progress bar with segments */}
        <div className="relative h-3 rounded-full bg-white/[0.04] overflow-hidden">
          <motion.div
            className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-primary to-accent"
            initial={{ width: 0 }}
            animate={{ width: `${lifePercent}%` }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
          />
          {/* Glow at tip */}
          <motion.div
            className="absolute top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-accent/30 blur-lg pointer-events-none"
            animate={{ left: `${lifePercent}%` }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
          />
        </div>

        {/* Live seconds counter */}
        <div className="mt-3 text-center">
          <span className="text-[10px] text-hint uppercase tracking-wider">{t('life.secondsLived')}</span>
          <motion.div
            key={seconds}
            initial={{ opacity: 0.7 }}
            animate={{ opacity: 1 }}
            className="text-lg font-display font-extrabold tabular-nums text-text mt-0.5"
          >
            {seconds.toLocaleString()}
          </motion.div>
        </div>
      </div>

      {/* ── Stats grid ── */}
      <div className="grid grid-cols-2 gap-2.5">
        <StatCard
          icon={<Calendar className="w-3.5 h-3.5" />}
          iconColor="#0061FF"
          label={t('life.daysLived')}
          value={daysLived.toLocaleString()}
        />
        <StatCard
          icon={<Clock className="w-3.5 h-3.5" />}
          iconColor="#EF4444"
          label={t('life.daysRemaining')}
          value={daysRemaining.toLocaleString()}
          accent
        />
        <StatCard
          icon={<Zap className="w-3.5 h-3.5" />}
          iconColor="#22C55E"
          label={t('life.weeksLived')}
          value={weeksLived.toLocaleString()}
        />
        <StatCard
          icon={<Zap className="w-3.5 h-3.5" />}
          iconColor="#F59E0B"
          label={t('life.weeksRemaining')}
          value={weeksRemaining.toLocaleString()}
        />
        <StatCard
          icon={<HeartPulse />}
          iconColor="#EC4899"
          label={t('life.heartbeats')}
          value={formatLargeNumber(heartbeats, lang)}
          colSpan2
        />
      </div>

      {/* ── Motivational quote ── */}
      <div className="relative overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3">
        <p className="text-xs text-hint italic text-center leading-relaxed">"{quote}"</p>
      </div>
    </div>
  )
})

// ── Stat card ──

function StatCard({
  icon,
  iconColor,
  label,
  value,
  accent = false,
  colSpan2 = false,
}: {
  icon: React.ReactNode
  iconColor: string
  label: string
  value: string
  accent?: boolean
  colSpan2?: boolean
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`
        relative overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.03] p-3
        ${colSpan2 ? 'col-span-2' : ''}
      `}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <div
          className="flex items-center justify-center w-6 h-6 rounded-md"
          style={{ backgroundColor: `${iconColor}15`, color: iconColor }}
        >
          {icon}
        </div>
        <span className="text-[10px] font-medium text-hint uppercase tracking-wider">{label}</span>
      </div>
      <span
        className={`text-base font-display font-extrabold tabular-nums ${accent ? 'text-danger' : 'text-text'}`}
      >
        {value}
      </span>
    </motion.div>
  )
}

// ── Heartbeat icon with pulse ──

function HeartPulse() {
  return (
    <motion.span
      animate={{ scale: [1, 1.2, 1] }}
      transition={{ duration: 0.8, repeat: Infinity, repeatDelay: 0.4 }}
    >
      <Heart className="w-3.5 h-3.5" fill="currentColor" />
    </motion.span>
  )
}
